import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiFetch } from '@/lib/api/client';
import { JOURNAL_TYPES, type JournalType } from '@/pages/safe/safeJournalApi';

import { SOURCE_LABELS } from './legacyApi';

/**
 * 사진 타임라인 · 검색(백엔드 SafeExploreController · DOC-136 · 사용자 결정 2026-10-28) — 읽기만.
 * 옛 기록(LEGACY) · 일지(JOURNAL) · 아차사고/의견(VOICE)을 한 줄로 받는다.
 */

export type ExploreKind = 'LEGACY' | 'JOURNAL' | 'VOICE';

/** 기록 하나를 가리키는 값 — LEGACY 면 type 은 옛 메뉴(source) · refId 는 code, JOURNAL 이면 일지 종류 · 일지 id. */
export type ExploreRef = { kind: ExploreKind; type: string; refId: string };

/** url 이 비면 아직 저장소에 올리지 않은 옛 사진. */
export type ExplorePhoto = ExploreRef & { id: string; day: string; url: string | null; caption: string | null };
export type ExplorePhotoPage = { items: ExplorePhoto[]; next: string | null };

export type ExploreHit = ExploreRef & {
  day: string | null;
  siteId: string | null;
  siteName: string | null;
  title: string | null;
  snippet: string | null;
};
export type ExploreHitPage = { page: number; size: number; hasMore: boolean; rows: ExploreHit[] };

const VOICE_LABELS: Record<string, { label: string; path: string }> = {
  NEAR_MISS: { label: '아차사고일지', path: '/near-miss' },
  OPINION: { label: '근로자 의견 청취', path: '/voice' },
};

/** 메뉴 이름 — 기존 데이터는 「기존 」 을 앞에 붙인다(기존 TBM · TBM 일지). */
export function refLabel(ref: Pick<ExploreRef, 'kind' | 'type'>): string {
  if (ref.kind === 'LEGACY') return `기존 ${SOURCE_LABELS[ref.type] ?? ref.type}`;
  if (ref.kind === 'VOICE') return VOICE_LABELS[ref.type]?.label ?? ref.type;
  return JOURNAL_TYPES[ref.type as JournalType]?.label ?? ref.type;
}

/**
 * 우리 기록을 여는 주소 — 일지는 그 종류 화면의 `?open=`(SafeJournalPage). 옛 기록은 주소가 없다(화면이 한 장을 띄운다).
 * 현장 없는 일지(본사)는 전체 화면의 종류 주소로.
 */
export function refHref(ref: ExploreRef, siteId: string | null): string | null {
  if (ref.kind === 'LEGACY') return null;
  const base = siteId ? `/sites/${encodeURIComponent(siteId)}` : '';
  if (ref.kind === 'VOICE') {
    const path = VOICE_LABELS[ref.type]?.path;
    return path && siteId ? `${base}${path}?open=${encodeURIComponent(ref.refId)}` : null;
  }
  const path = JOURNAL_TYPES[ref.type as JournalType]?.path;
  return path ? `${base}${path}?open=${encodeURIComponent(ref.refId)}` : null;
}

/** 기록 열기 — 우리 기록은 그 화면으로 옮기고, 옛 기록은 peek 에 담아 화면이 LegacyRecordModal 을 띄운다. */
export function useOpenRef() {
  const navigate = useNavigate();
  const [peek, setPeek] = useState<{ source: string; code: string } | null>(null);
  const open = (ref: ExploreRef, siteId: string | null) => {
    if (ref.kind === 'LEGACY') {
      setPeek({ source: ref.type, code: ref.refId });
      return;
    }
    const href = refHref(ref, siteId);
    if (href) navigate(href);
  };
  return { open, peek, closePeek: () => setPeek(null) };
}

const qs = (params: Record<string, string | number | null | undefined>) =>
  new URLSearchParams(Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => [k, String(v)])).toString();

export function useSitePhotos(siteId: string) {
  return useInfiniteQuery({
    queryKey: ['explore', 'photos', siteId],
    initialPageParam: '' as string,
    queryFn: ({ pageParam }) =>
      apiFetch<ExplorePhotoPage>(`/v1/safe/sites/${encodeURIComponent(siteId)}/photos?${qs({ before: pageParam, limit: 60 })}`),
    getNextPageParam: (last) => last.next ?? undefined,
  });
}

export type SearchParams = { q: string; kind: ExploreKind | ''; siteId?: string; from: string; to: string; page: number };

export function useSafeSearch(params: SearchParams) {
  const q = params.q.trim();
  return useQuery({
    queryKey: ['explore', 'search', q, params.kind, params.siteId ?? null, params.from, params.to, params.page],
    queryFn: () =>
      apiFetch<ExploreHitPage>(
        `/v1/safe/search?${qs({ q, kind: params.kind, siteId: params.siteId, from: params.from, to: params.to, page: params.page })}`,
      ),
    // 빈 검색어는 최근 기록(서버가 날짜 최신순으로 준다). 한 글자만 막는다.
    enabled: [...q].length !== 1,
    placeholderData: keepPreviousData,
  });
}
